import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { EmployeeProfile } from '../types/employee';

/**
 * Shared selection of pickers (associate logins) that persists across the
 * Picker Eligibilities and Cluster Eligibilities pages.
 *
 * Why this exists: each page used to hold its own local `selectedPickers`
 * state, so navigating from one page to the other dropped the selection and the
 * associate had to re-type every login. This context lifts the selection to the
 * app level so both pages read/write the same list.
 *
 * Scope rules:
 *  - In-memory only for the lifetime of the page (matches legacy AngularJS
 *    behavior, where the selection lived only in $scope). A page reload clears
 *    the selection — it is intentionally NOT persisted to storage.
 *  - Cleared on the `warehouseChanged` event to avoid carrying logins from one
 *    FC into another.
 */
interface SelectedPickersContextValue {
    selectedPickers: EmployeeProfile[];
    setSelectedPickers: (pickers: EmployeeProfile[]) => void;
    clearSelectedPickers: () => void;
}

const SelectedPickersContext = createContext<SelectedPickersContextValue | undefined>(undefined);

export const SelectedPickersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedPickers, setSelectedPickersState] = useState<EmployeeProfile[]>([]);

    const setSelectedPickers = useCallback((pickers: EmployeeProfile[]) => {
        setSelectedPickersState(pickers);
    }, []);

    const clearSelectedPickers = useCallback(() => {
        setSelectedPickersState([]);
    }, []);

    // Clear the selection when the warehouse changes — logins are FC-specific.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
        const onWarehouseChanged = () => {
            setSelectedPickersState([]);
        };
        window.addEventListener('warehouseChanged', onWarehouseChanged);
        return () => window.removeEventListener('warehouseChanged', onWarehouseChanged);
    }, []);

    return (
        <SelectedPickersContext.Provider value={{ selectedPickers, setSelectedPickers, clearSelectedPickers }}>
            {children}
        </SelectedPickersContext.Provider>
    );
};

/**
 * Access the shared picker selection. When used outside the provider (e.g.
 * isolated component tests) it falls back to local in-memory state so consumers
 * never crash on a missing provider.
 *
 * All hooks are called unconditionally on every render to satisfy the Rules of
 * Hooks; the local fallback state is simply unused when the provider is present.
 */
export function useSelectedPickers(): SelectedPickersContextValue {
    const ctx = useContext(SelectedPickersContext);

    const [localPickers, setLocalPickers] = useState<EmployeeProfile[]>([]);
    const setSelectedPickers = useCallback((pickers: EmployeeProfile[]) => setLocalPickers(pickers), []);
    const clearSelectedPickers = useCallback(() => setLocalPickers([]), []);

    if (ctx) return ctx;
    return { selectedPickers: localPickers, setSelectedPickers, clearSelectedPickers };
}
